import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Calendar,
  Eye,
  FileDown,
  Plus,
  RefreshCcw,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { QuotationGeneratorAPI } from "api/quotationGenerator/service";

const DEFAULT_TEMPLATE = {
  name: "Modern Quotation",
  css: `
/* Tip: keep styles scoped to .qt-preview */
.qt-preview { font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: #111827; }
.qt-preview .qt-page { width: 794px; min-height: 1123px; margin: 0 auto; background: #fff; padding: 42px; box-sizing: border-box; }
.qt-preview .qt-top { display:flex; justify-content: space-between; gap: 16px; align-items: flex-start; }
.qt-preview .qt-brand { font-weight: 700; font-size: 18px; color: #3B0069; }
.qt-preview .qt-meta { text-align:right; font-size: 12px; color: #6B7280; }
.qt-preview .qt-h1 { font-size: 28px; font-weight: 800; margin: 18px 0 8px; }
.qt-preview .qt-sub { font-size: 12px; color: #6B7280; margin-bottom: 18px; }
.qt-preview .qt-grid { display:grid; grid-template-columns: 1fr 1fr; gap: 14px; margin: 18px 0; }
.qt-preview .qt-card { border: 1px solid #E5E7EB; border-radius: 12px; padding: 14px; }
.qt-preview .qt-card h3 { margin: 0 0 8px; font-size: 12px; letter-spacing: .06em; text-transform: uppercase; color:#6B7280; }
.qt-preview .qt-kv { font-size: 13px; color: #111827; line-height: 1.5; }
.qt-preview .qt-divider { height: 1px; background: #E5E7EB; margin: 18px 0; }
.qt-preview .qt-section h2 { font-size: 14px; margin: 0 0 8px; }
.qt-preview ul { margin: 0; padding-left: 18px; }
.qt-preview li { margin: 4px 0; }
.qt-preview .qt-images { display:flex; flex-wrap: wrap; gap: 10px; margin-top: 10px; }
.qt-preview .qt-images img { width: 180px; height: 120px; object-fit: cover; border-radius: 10px; border: 1px solid #E5E7EB; }
.qt-preview .qt-footer { margin-top: 28px; font-size: 12px; color:#6B7280; }
  `.trim(),
  html: `
<div class="qt-preview">
  <div class="qt-page">
    <div class="qt-top">
      <div>
        <div class="qt-brand">{{organization_name}}</div>
        <div class="qt-sub">{{organization_address}}</div>
      </div>
      <div class="qt-meta">
        <div><strong>Quotation</strong></div>
        <div>Date: {{today_date}}</div>
        <div>Project: {{project_name}}</div>
      </div>
    </div>

    <div class="qt-h1">Quotation</div>
    <div class="qt-sub">Prepared for <strong>{{client_name}}</strong></div>

    <div class="qt-grid">
      <div class="qt-card">
        <h3>Client</h3>
        <div class="qt-kv">{{client_name}}</div>
        <div class="qt-kv">{{client_address}}</div>
        <div class="qt-kv">{{client_logo}}</div>
      </div>
      <div class="qt-card">
        <h3>Project</h3>
        <div class="qt-kv"><strong>Name:</strong> {{project_name}}</div>
        <div class="qt-kv"><strong>Address:</strong> {{project_address}}</div>
        <div class="qt-kv"><strong>Area:</strong> {{project_area}}</div>
        <div class="qt-kv"><strong>Start Date:</strong> {{project_start_date}}</div>
        <div class="qt-kv"><strong>ETA:</strong> {{estimated_completion_time}}</div>
      </div>
    </div>

    <div class="qt-divider"></div>

    <div class="qt-section">
      <h2>Scope of Work</h2>
      {{scope_of_work}}
    </div>

    <div class="qt-divider"></div>

    <div class="qt-section">
      <h2>Project Stages</h2>
      {{project_stages}}
    </div>

    <div class="qt-divider"></div>

    <div class="qt-section">
      <h2>Site / Reference Images</h2>
      {{images}}
    </div>

    <div class="qt-divider"></div>

    <div class="qt-section">
      <h2>Terms & Conditions</h2>
      <div class="qt-kv">{{terms_and_conditions}}</div>
    </div>

    <div class="qt-footer">Generated using SiteMaster Quotation Generator</div>
  </div>
</div>
  `.trim(),
};

const PLACEHOLDERS = [
  { key: "client_name", label: "Client Name" },
  { key: "client_address", label: "Client Address" },
  { key: "client_logo", label: "Client Logo (auto)" },
  { key: "project_name", label: "Project Name" },
  { key: "project_address", label: "Project Address" },
  { key: "project_area", label: "Project Area" },
  { key: "project_coordinates", label: "Project Coordinates" },
  { key: "project_start_date", label: "Project Start Date" },
  { key: "estimated_completion_time", label: "ETA / Duration" },
  { key: "scope_of_work", label: "Scope of Work (list)" },
  { key: "project_stages", label: "Project Stages (list)" },
  { key: "terms_and_conditions", label: "Terms & Conditions" },
  { key: "images", label: "Images Gallery (auto)" },
];

function safeText(v) {
  if (v == null) return "";
  return String(v);
}

function formatList(value) {
  const arr = Array.isArray(value) ? value : value ? [value] : [];
  if (!arr.length) return "";
  const items = arr
    .map((x) => safeText(x).trim())
    .filter(Boolean)
    .map((x) => `<li>${x}</li>`)
    .join("");
  return items ? `<ul>${items}</ul>` : "";
}

function compileTemplate(html, data) {
  const src = safeText(html || "");
  return src.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
    const v = data?.[key];
    if (v == null) return "";
    if (typeof v === "string") return v;
    if (Array.isArray(v)) return formatList(v);
    return safeText(v);
  });
}

function wrapPreviewDocument(css, html) {
  const styleTag = `<style>${safeText(css || "")}</style>`;
  return `${styleTag}${safeText(html || "")}`;
}

async function downloadPdfFromNode(node, filename) {
  if (!node) throw new Error("Nothing to export.");
  const canvas = await html2canvas(node, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: "#ffffff",
  });
  const imgData = canvas.toDataURL("image/png");

  const pdf = new jsPDF("p", "pt", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let position = 0;
  pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);

  let heightLeft = imgHeight - pageHeight;
  while (heightLeft > 0) {
    position -= pageHeight;
    pdf.addPage();
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  pdf.save(filename);
}

const Modal = ({ isOpen, title, onClose, children, maxWidthClass, maxHeightClass }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div
        className={`relative bg-white rounded-xl shadow-xl w-[92vw] ${maxWidthClass || "max-w-6xl"} ${
          maxHeightClass || "max-h-[85vh]"
        } overflow-hidden flex flex-col`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex flex-col">
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500">Design, preview, and publish in a few clicks.</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-700"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
};

const Quotationgenerator = () => {
  const queryClient = useQueryClient();
  const tabPreviewRef = useRef(null);
  const modalPreviewRef = useRef(null);
  const [activeTab, setActiveTab] = useState("templates"); // templates | quotations
  const [templatesSearch, setTemplatesSearch] = useState("");
  const [quotationsSearch, setQuotationsSearch] = useState("");

  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [createQuotationOpen, setCreateQuotationOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  const organization = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("organization") || "{}");
    } catch (_) {
      return {};
    }
  }, []);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch (_) {
      return {};
    }
  }, []);

  const createdBy =
    user?.first_name ||
    user?.user_email ||
    user?.email ||
    (typeof user?.user === "string" ? user.user : "system");

  // -------- Templates --------
  const [templateDraft, setTemplateDraft] = useState({
    name: DEFAULT_TEMPLATE.name,
    html: DEFAULT_TEMPLATE.html,
    css: DEFAULT_TEMPLATE.css,
  });

  const templatesQuery = useQuery({
    queryKey: ["quotation-templates"],
    queryFn: async () => {
      const res = await QuotationGeneratorAPI.listTemplates();
      return res?.data || [];
    },
  });

  const saveTemplateMutation = useMutation({
    mutationFn: async () => {
      const orgId = QuotationGeneratorAPI.orgId();
      if (!orgId) throw new Error("Organization not found in local storage.");

      const payload = {
        organization: orgId,
        name: String(templateDraft.name || "").trim(),
        html_content: templateDraft.html,
        styles: { css: templateDraft.css },
        created_by: createdBy,
        last_updated_by: createdBy,
      };

      if (!payload.name) throw new Error("Template name is required.");

      if (editingTemplateId) {
        return await QuotationGeneratorAPI.updateTemplate(editingTemplateId, payload);
      }
      return await QuotationGeneratorAPI.createTemplate(payload);
    },
    onSuccess: async () => {
      toast.success(editingTemplateId ? "Template updated" : "Template created");
      setCreateTemplateOpen(false);
      setEditingTemplateId(null);
      await queryClient.invalidateQueries({ queryKey: ["quotation-templates"] });
    },
    onError: (e) => {
      toast.error(e?.message || "Failed to save template");
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id) => QuotationGeneratorAPI.deleteTemplate(id),
    onSuccess: async () => {
      toast.success("Template deleted");
      await queryClient.invalidateQueries({ queryKey: ["quotation-templates"] });
    },
    onError: (e) => toast.error(e?.message || "Failed to delete template"),
  });

  const filteredTemplates = useMemo(() => {
    const q = templatesSearch.trim().toLowerCase();
    const list = templatesQuery.data || [];
    if (!q) return list;
    return list.filter((t) => safeText(t?.name).toLowerCase().includes(q));
  }, [templatesQuery.data, templatesSearch]);

  // -------- Quotations --------
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [quotationDraft, setQuotationDraft] = useState({
    client_name: "",
    client_address: "",
    client_logo_file: null,
    project_name: "",
    project_address: "",
    project_area: "",
    project_coordinates: "",
    project_start_date: "",
    estimated_completion_time: "",
    scope_of_work_items: ["Planning & Design", "Execution", "Maintenance"],
    project_stages_items: ["Stage 1", "Stage 2", "Stage 3"],
    terms_and_conditions:
      "This quotation is valid for 15 days. Any changes in scope may impact pricing and timelines.",
    images_files: [],
  });

  const quotationsQuery = useQuery({
    queryKey: ["project-quotations"],
    queryFn: async () => {
      const res = await QuotationGeneratorAPI.listQuotations();
      return res?.data || [];
    },
  });

  const createQuotationMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTemplateId) throw new Error("Please select a template.");

      const fd = new FormData();
      fd.append("template", String(selectedTemplateId));

      // Client
      fd.append("client_name", quotationDraft.client_name || "");
      fd.append("client_address", quotationDraft.client_address || "");
      if (quotationDraft.client_logo_file) {
        fd.append("client_logo", quotationDraft.client_logo_file);
      }

      // Project
      fd.append("project_name", quotationDraft.project_name || "");
      fd.append("project_address", quotationDraft.project_address || "");
      fd.append("project_area", quotationDraft.project_area || "");
      fd.append("project_coordinates", quotationDraft.project_coordinates || "");
      if (quotationDraft.project_start_date) {
        fd.append("project_start_date", quotationDraft.project_start_date);
      }
      fd.append("estimated_completion_time", quotationDraft.estimated_completion_time || "");

      // Flexible fields
      fd.append("scope_of_work", JSON.stringify(quotationDraft.scope_of_work_items || []));
      fd.append("project_stages", JSON.stringify(quotationDraft.project_stages_items || []));
      fd.append("terms_and_conditions", quotationDraft.terms_and_conditions || "");

      // Images (multiple)
      (quotationDraft.images_files || []).forEach((f) => fd.append("images", f));

      fd.append("created_by", createdBy);
      fd.append("last_updated_by", createdBy);

      const res = await QuotationGeneratorAPI.createQuotation(fd);
      return res;
    },
    onSuccess: async () => {
      toast.success("Quotation created");
      setCreateQuotationOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["project-quotations"] });
    },
    onError: (e) => {
      toast.error(e?.message || "Failed to create quotation");
    },
  });

  const filteredQuotations = useMemo(() => {
    const q = quotationsSearch.trim().toLowerCase();
    const list = quotationsQuery.data || [];
    if (!q) return list;
    return list.filter((x) => {
      const a = safeText(x?.project_name).toLowerCase();
      const b = safeText(x?.client_name).toLowerCase();
      return a.includes(q) || b.includes(q);
    });
  }, [quotationsQuery.data, quotationsSearch]);

  const selectedTemplate = useMemo(() => {
    const list = templatesQuery.data || [];
    return list.find((t) => String(t?.id) === String(selectedTemplateId)) || null;
  }, [templatesQuery.data, selectedTemplateId]);

  const clientLogoUrl = useMemo(() => {
    if (!quotationDraft.client_logo_file) return "";
    try {
      return URL.createObjectURL(quotationDraft.client_logo_file);
    } catch (_) {
      return "";
    }
  }, [quotationDraft.client_logo_file]);

  useEffect(() => {
    return () => {
      if (clientLogoUrl) URL.revokeObjectURL(clientLogoUrl);
    };
  }, [clientLogoUrl]);

  const [imageObjectUrls, setImageObjectUrls] = useState([]);
  useEffect(() => {
    const files = quotationDraft.images_files || [];
    const urls = files
      .map((f) => {
        try {
          return URL.createObjectURL(f);
        } catch (_) {
          return null;
        }
      })
      .filter(Boolean);

    setImageObjectUrls(urls);
    return () => {
      urls.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {
          // ignore
        }
      });
    };
  }, [quotationDraft.images_files]);

  const imagesHtml = useMemo(() => {
    if (!imageObjectUrls.length) return `<div class="qt-kv">No images attached.</div>`;
    const imgs = imageObjectUrls.map((u) => `<img src="${u}" alt="quotation" />`).join("");
    return `<div class="qt-images">${imgs}</div>`;
  }, [imageObjectUrls]);

  const previewDoc = useMemo(() => {
    const tplHtml = selectedTemplate?.html_content || DEFAULT_TEMPLATE.html;
    const tplCss =
      selectedTemplate?.styles?.css ||
      (typeof selectedTemplate?.styles === "string" ? selectedTemplate.styles : "") ||
      DEFAULT_TEMPLATE.css;

    const today = new Date();
    const data = {
      organization_name: safeText(organization?.organization_name || "Your Organization"),
      organization_address: safeText(organization?.address || ""),
      today_date: today.toLocaleDateString(),

      client_name: safeText(quotationDraft.client_name),
      client_address: safeText(quotationDraft.client_address),
      client_logo: clientLogoUrl
        ? `<img src="${clientLogoUrl}" alt="client-logo" style="max-width: 180px; max-height: 72px; object-fit: contain;" />`
        : "",

      project_name: safeText(quotationDraft.project_name),
      project_address: safeText(quotationDraft.project_address),
      project_area: safeText(quotationDraft.project_area),
      project_coordinates: safeText(quotationDraft.project_coordinates),
      project_start_date: safeText(quotationDraft.project_start_date),
      estimated_completion_time: safeText(quotationDraft.estimated_completion_time),

      scope_of_work: formatList(quotationDraft.scope_of_work_items),
      project_stages: formatList(quotationDraft.project_stages_items),
      terms_and_conditions: safeText(quotationDraft.terms_and_conditions),
      images: imagesHtml,
    };

    const compiled = compileTemplate(tplHtml, data);
    return wrapPreviewDocument(tplCss, compiled);
  }, [selectedTemplate, quotationDraft, organization, clientLogoUrl, imagesHtml]);

  const insertPlaceholderIntoHtml = (placeholderKey) => {
    const token = `{{${placeholderKey}}}`;
    setTemplateDraft((p) => ({ ...p, html: `${p.html}${p.html ? "\n" : ""}${token}` }));
  };

  return (
    <div className="w-[85%] py-4 px-4 ml-2 h-[90vh] overflow-y-auto">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Quotation Generator</h2>
          <p className="text-sm text-gray-600">
            Build templates, generate quotations, and export polished PDFs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm flex items-center gap-2"
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ["quotation-templates"] });
              queryClient.invalidateQueries({ queryKey: ["project-quotations"] });
            }}
          >
            <RefreshCcw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 bg-white border border-gray-200 rounded-xl p-1 inline-flex gap-1">
        <button
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            activeTab === "templates" ? "bg-[#3B0069] text-white" : "text-gray-700 hover:bg-gray-50"
          }`}
          onClick={() => setActiveTab("templates")}
        >
          Templates
        </button>
        <button
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            activeTab === "quotations" ? "bg-[#3B0069] text-white" : "text-gray-700 hover:bg-gray-50"
          }`}
          onClick={() => setActiveTab("quotations")}
        >
          Quotations
        </button>
      </div>

      {/* Templates tab */}
      {activeTab === "templates" && (
        <div className="mt-4 grid grid-cols-12 gap-4">
          <div className="col-span-12 xl:col-span-5">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Templates</h3>
                  <p className="text-xs text-gray-500">Create and manage reusable layouts.</p>
                </div>
                <button
                  onClick={() => {
                    setTemplateDraft({
                      name: DEFAULT_TEMPLATE.name,
                      html: DEFAULT_TEMPLATE.html,
                      css: DEFAULT_TEMPLATE.css,
                    });
                    setEditingTemplateId(null);
                    setCreateTemplateOpen(true);
                  }}
                  className="px-3 py-2 rounded-lg bg-[#3B0069] text-white hover:bg-purple-800 text-sm flex items-center gap-2"
                >
                  <Plus size={16} />
                  New Template
                </button>
              </div>

              <div className="p-4">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={templatesSearch}
                    onChange={(e) => setTemplatesSearch(e.target.value)}
                    placeholder="Search templates..."
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                  />
                </div>
              </div>

              <div className="max-h-[56vh] overflow-y-auto">
                {templatesQuery.isLoading ? (
                  <div className="p-4 text-sm text-gray-600">Loading templates...</div>
                ) : filteredTemplates.length === 0 ? (
                  <div className="p-6">
                    <div className="border border-dashed border-gray-300 rounded-xl p-6 text-center">
                      <p className="text-sm font-medium text-gray-900">No templates yet</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Create your first template with the live editor.
                      </p>
                      <button
                        onClick={() => setCreateTemplateOpen(true)}
                        className="mt-4 px-3 py-2 rounded-lg bg-[#3B0069] text-white hover:bg-purple-800 text-sm inline-flex items-center gap-2"
                      >
                        <Plus size={16} /> Create Template
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredTemplates.map((t) => (
                      <div
                        key={t.id}
                        className={`p-4 flex items-start justify-between gap-3 cursor-pointer hover:bg-gray-50 ${
                          String(editingTemplateId) === String(t.id) ? "bg-purple-50" : ""
                        }`}
                        onClick={() => {
                          setTemplateDraft({
                            name: safeText(t?.name),
                            html: safeText(t?.html_content || ""),
                            css:
                              t?.styles?.css ||
                              (typeof t?.styles === "string" ? t.styles : "") ||
                              "",
                          });
                          setEditingTemplateId(t.id);
                        }}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{t?.name}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <Calendar size={14} />
                            <span>{safeText(t?.last_updated_on || t?.created_on || "")}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCreateTemplateOpen(true);
                            }}
                            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm"
                            title="Edit template"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteTemplateMutation.mutate(t.id);
                            }}
                            className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-red-50 hover:border-red-200 text-gray-700 hover:text-red-700"
                            title="Delete template"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-span-12 xl:col-span-7">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Template Preview</h3>
                  <p className="text-xs text-gray-500">
                    Use placeholders like <span className="font-mono">{`{{client_name}}`}</span>.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCreateTemplateOpen(true)}
                    className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm flex items-center gap-2"
                  >
                    <Eye size={16} />
                    {editingTemplateId ? "Edit Template" : "Open Editor"}
                  </button>
                </div>
              </div>
              <div className="p-4 bg-gray-50">
                <div
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                  dangerouslySetInnerHTML={{
                    __html: wrapPreviewDocument(templateDraft.css, templateDraft.html),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quotations tab */}
      {activeTab === "quotations" && (
        <div className="mt-4 grid grid-cols-12 gap-4">
          <div className="col-span-12 xl:col-span-5">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Quotations</h3>
                  <p className="text-xs text-gray-500">Create and export quotations from templates.</p>
                </div>
                <button
                  onClick={() => setCreateQuotationOpen(true)}
                  className="px-3 py-2 rounded-lg bg-[#3B0069] text-white hover:bg-purple-800 text-sm flex items-center gap-2"
                >
                  <Plus size={16} />
                  New Quotation
                </button>
              </div>

              <div className="p-4">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={quotationsSearch}
                    onChange={(e) => setQuotationsSearch(e.target.value)}
                    placeholder="Search by project or client..."
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                  />
                </div>
              </div>

              <div className="max-h-[56vh] overflow-y-auto">
                {quotationsQuery.isLoading ? (
                  <div className="p-4 text-sm text-gray-600">Loading quotations...</div>
                ) : filteredQuotations.length === 0 ? (
                  <div className="p-6">
                    <div className="border border-dashed border-gray-300 rounded-xl p-6 text-center">
                      <p className="text-sm font-medium text-gray-900">No quotations yet</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Create a quotation using any template and export as PDF.
                      </p>
                      <button
                        onClick={() => setCreateQuotationOpen(true)}
                        className="mt-4 px-3 py-2 rounded-lg bg-[#3B0069] text-white hover:bg-purple-800 text-sm inline-flex items-center gap-2"
                      >
                        <Plus size={16} /> Create Quotation
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredQuotations.map((q) => (
                      <div key={q.id} className="p-4">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {safeText(q?.project_name) || "Untitled Project"}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Client: {safeText(q?.client_name) || "—"}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-span-12 xl:col-span-7">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Live Preview</h3>
                  <p className="text-xs text-gray-500">
                    Select a template and fill details to preview instantly.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      try {
                        await downloadPdfFromNode(
                          tabPreviewRef.current,
                          `${(quotationDraft.project_name || "quotation").replace(/\s+/g, "-")}.pdf`
                        );
                      } catch (e) {
                        toast.error(e?.message || "Failed to export PDF");
                      }
                    }}
                    className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm flex items-center gap-2"
                  >
                    <FileDown size={16} />
                    Download PDF
                  </button>
                  <button
                    onClick={() => setCreateQuotationOpen(true)}
                    className="px-3 py-2 rounded-lg bg-[#3B0069] text-white hover:bg-purple-800 text-sm flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Create
                  </button>
                </div>
              </div>

              <div className="p-4 bg-gray-50 overflow-x-auto">
                <div
                  ref={tabPreviewRef}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                  dangerouslySetInnerHTML={{ __html: previewDoc }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create template modal */}
      <Modal
        isOpen={createTemplateOpen}
        onClose={() => setCreateTemplateOpen(false)}
        title={editingTemplateId ? "Edit Template" : "Create Template"}
        maxWidthClass="max-w-7xl"
        maxHeightClass="max-h-[80vh]"
      >
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-5 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <label className="text-sm font-medium text-gray-700">Template name</label>
              <input
                value={templateDraft.name}
                onChange={(e) => setTemplateDraft((p) => ({ ...p, name: e.target.value }))}
                className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                placeholder="e.g., Premium Quotation"
              />

              <div className="mt-4 flex items-center justify-between">
                <p className="text-xs text-gray-500">Quick insert</p>
                <button
                  onClick={() =>
                    setTemplateDraft({
                      name: DEFAULT_TEMPLATE.name,
                      html: DEFAULT_TEMPLATE.html,
                      css: DEFAULT_TEMPLATE.css,
                    })
                  }
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                >
                  Load sample
                </button>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                {PLACEHOLDERS.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => insertPlaceholderIntoHtml(p.key)}
                    className="text-left px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                    title={`Insert {{${p.key}}}`}
                  >
                    <p className="text-xs font-semibold text-gray-900 truncate">{p.label}</p>
                    <p className="text-[11px] text-gray-500 font-mono truncate">{`{{${p.key}}}`}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900">Styles (CSS)</h4>
                <p className="text-xs text-gray-500">
                  Stored in <span className="font-mono">styles</span> JSON
                </p>
              </div>
              <textarea
                value={templateDraft.css}
                onChange={(e) => setTemplateDraft((p) => ({ ...p, css: e.target.value }))}
                className="mt-3 w-full h-[220px] font-mono text-xs p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                placeholder=".qt-preview { ... }"
              />
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900">HTML Content</h4>
                <p className="text-xs text-gray-500">
                  Saved as <span className="font-mono">html_content</span>
                </p>
              </div>
              <textarea
                value={templateDraft.html}
                onChange={(e) => setTemplateDraft((p) => ({ ...p, html: e.target.value }))}
                className="mt-3 w-full h-[280px] font-mono text-xs p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                placeholder="<div>...</div>"
              />
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Live Preview</h4>
                  <p className="text-xs text-gray-500">What you see is what you publish.</p>
                </div>
                <button
                  onClick={() => saveTemplateMutation.mutate()}
                  disabled={saveTemplateMutation.isPending}
                  className="px-3 py-2 rounded-lg bg-[#3B0069] text-white hover:bg-purple-800 text-sm flex items-center gap-2 disabled:opacity-60"
                >
                  <Save size={16} />
                  {saveTemplateMutation.isPending
                    ? "Saving..."
                    : editingTemplateId
                      ? "Update Template"
                      : "Save Template"}
                </button>
              </div>
              <div className="p-4 bg-gray-50 overflow-x-auto">
                <div
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                  dangerouslySetInnerHTML={{
                    __html: wrapPreviewDocument(templateDraft.css, templateDraft.html),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Create quotation modal */}
      <Modal
        isOpen={createQuotationOpen}
        onClose={() => setCreateQuotationOpen(false)}
        title="Create Quotation"
        maxWidthClass="max-w-7xl"
      >
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-5 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <label className="text-sm font-medium text-gray-700">Template</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
              >
                <option value="">Select a template</option>
                {(templatesQuery.data || []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Tip: Use the Templates tab to build multiple branded formats.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-900">Client details</h4>
              <div className="mt-3 space-y-3">
                <input
                  value={quotationDraft.client_name}
                  onChange={(e) => setQuotationDraft((p) => ({ ...p, client_name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                  placeholder="Client name"
                />
                <input
                  value={quotationDraft.client_address}
                  onChange={(e) =>
                    setQuotationDraft((p) => ({ ...p, client_address: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                  placeholder="Client address"
                />

                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Client logo (optional)</p>
                    <p className="text-xs text-gray-500">Used for preview + upload.</p>
                  </div>
                  <label className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm cursor-pointer">
                    Upload
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setQuotationDraft((p) => ({ ...p, client_logo_file: f }));
                        if (e.target) e.target.value = "";
                      }}
                    />
                  </label>
                </div>

                {quotationDraft.client_logo_file && (
                  <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                    <p className="text-xs text-gray-700 truncate">
                      {quotationDraft.client_logo_file.name}
                    </p>
                    <button
                      onClick={() => setQuotationDraft((p) => ({ ...p, client_logo_file: null }))}
                      className="p-2 rounded-lg hover:bg-gray-50"
                      title="Remove"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-900">Project details</h4>
              <div className="mt-3 space-y-3">
                <input
                  value={quotationDraft.project_name}
                  onChange={(e) => setQuotationDraft((p) => ({ ...p, project_name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                  placeholder="Project name"
                />
                <input
                  value={quotationDraft.project_address}
                  onChange={(e) =>
                    setQuotationDraft((p) => ({ ...p, project_address: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                  placeholder="Project address"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    value={quotationDraft.project_area}
                    onChange={(e) =>
                      setQuotationDraft((p) => ({ ...p, project_area: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                    placeholder="Project area"
                  />
                  <input
                    value={quotationDraft.project_coordinates}
                    onChange={(e) =>
                      setQuotationDraft((p) => ({ ...p, project_coordinates: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                    placeholder="Coordinates"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    value={quotationDraft.project_start_date}
                    onChange={(e) =>
                      setQuotationDraft((p) => ({ ...p, project_start_date: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                  />
                  <input
                    value={quotationDraft.estimated_completion_time}
                    onChange={(e) =>
                      setQuotationDraft((p) => ({ ...p, estimated_completion_time: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                    placeholder="ETA (e.g., 6 months)"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-900">Scope of work</h4>
              <div className="mt-3 space-y-2">
                {(quotationDraft.scope_of_work_items || []).map((item, idx) => (
                  <div key={`${idx}-${item}`} className="flex items-center gap-2">
                    <input
                      value={item}
                      onChange={(e) => {
                        const v = e.target.value;
                        setQuotationDraft((p) => {
                          const next = [...(p.scope_of_work_items || [])];
                          next[idx] = v;
                          return { ...p, scope_of_work_items: next };
                        });
                      }}
                      className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                      placeholder="Item"
                    />
                    <button
                      onClick={() => {
                        setQuotationDraft((p) => {
                          const next = [...(p.scope_of_work_items || [])];
                          next.splice(idx, 1);
                          return { ...p, scope_of_work_items: next };
                        });
                      }}
                      className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-red-50 hover:border-red-200 text-gray-700 hover:text-red-700"
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() =>
                    setQuotationDraft((p) => ({
                      ...p,
                      scope_of_work_items: [...(p.scope_of_work_items || []), ""],
                    }))
                  }
                  className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2"
                >
                  <Plus size={16} /> Add item
                </button>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-900">Project stages</h4>
              <div className="mt-3 space-y-2">
                {(quotationDraft.project_stages_items || []).map((item, idx) => (
                  <div key={`${idx}-${item}`} className="flex items-center gap-2">
                    <input
                      value={item}
                      onChange={(e) => {
                        const v = e.target.value;
                        setQuotationDraft((p) => {
                          const next = [...(p.project_stages_items || [])];
                          next[idx] = v;
                          return { ...p, project_stages_items: next };
                        });
                      }}
                      className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                      placeholder="Stage"
                    />
                    <button
                      onClick={() => {
                        setQuotationDraft((p) => {
                          const next = [...(p.project_stages_items || [])];
                          next.splice(idx, 1);
                          return { ...p, project_stages_items: next };
                        });
                      }}
                      className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-red-50 hover:border-red-200 text-gray-700 hover:text-red-700"
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() =>
                    setQuotationDraft((p) => ({
                      ...p,
                      project_stages_items: [...(p.project_stages_items || []), ""],
                    }))
                  }
                  className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2"
                >
                  <Plus size={16} /> Add stage
                </button>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-900">Terms & Conditions</h4>
              <textarea
                value={quotationDraft.terms_and_conditions}
                onChange={(e) =>
                  setQuotationDraft((p) => ({ ...p, terms_and_conditions: e.target.value }))
                }
                className="mt-3 w-full h-[120px] p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200 text-sm"
              />
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Images (optional)</h4>
                  <p className="text-xs text-gray-500">Upload multiple files. Used for preview + upload.</p>
                </div>
                <label className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm cursor-pointer">
                  Add Images
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (!files.length) return;
                      setQuotationDraft((p) => ({
                        ...p,
                        images_files: [...(p.images_files || []), ...files],
                      }));
                      if (e.target) e.target.value = "";
                    }}
                  />
                </label>
              </div>

              {(quotationDraft.images_files || []).length > 0 && (
                <div className="mt-3 space-y-2">
                  {(quotationDraft.images_files || []).map((f, idx) => (
                    <div key={`${f.name}-${idx}`} className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2">
                      <p className="text-xs text-gray-700 truncate">{f.name}</p>
                      <button
                        onClick={() => {
                          setQuotationDraft((p) => {
                            const next = [...(p.images_files || [])];
                            next.splice(idx, 1);
                            return { ...p, images_files: next };
                          });
                        }}
                        className="p-2 rounded-lg hover:bg-gray-50"
                        title="Remove"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setCreateQuotationOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => createQuotationMutation.mutate()}
                disabled={createQuotationMutation.isPending}
                className="px-4 py-2 rounded-lg bg-[#3B0069] text-white hover:bg-purple-800 text-sm flex items-center gap-2 disabled:opacity-60"
              >
                <Save size={16} />
                {createQuotationMutation.isPending ? "Saving..." : "Save quotation"}
              </button>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Preview</h4>
                  <p className="text-xs text-gray-500">This is the exported PDF layout.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      try {
                        await downloadPdfFromNode(
                          modalPreviewRef.current,
                          `${(quotationDraft.project_name || "quotation").replace(/\s+/g, "-")}.pdf`
                        );
                      } catch (e) {
                        toast.error(e?.message || "Failed to export PDF");
                      }
                    }}
                    className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm flex items-center gap-2"
                  >
                    <FileDown size={16} />
                    Download PDF
                  </button>
                </div>
              </div>
              <div className="p-4 bg-gray-50 overflow-x-auto">
                <div
                  ref={modalPreviewRef}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                  dangerouslySetInnerHTML={{ __html: previewDoc }}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Quotationgenerator;
